import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import {
  selectUnreadCount,
  selectIsConnected,
  selectIsAuthenticated,
  selectIsWidgetOpen,
  setWidgetOpen,
  closeActiveConversation
} from '../../store/slices/chatSlice';
import { selectUser } from '../../store/slices/authSlice';
import { fetchUnreadCount } from '../../store/thunks/chatThunk';
import socketService from '../../services/socketService';
import ChatWidget from './ChatWidget';

const ChatButton = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const unreadCount = useSelector(selectUnreadCount);
  const isConnected = useSelector(selectIsConnected);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isWidgetOpen = useSelector(selectIsWidgetOpen);
  
  const [isInitialized, setIsInitialized] = useState(false);

  // Check if user has admin privileges
  const hasAdminAccess = user && user.role && (
    user.role.includes('admin') || 
    user.role.includes('owner') || 
    user.role.includes('officeManager')
  );

  // Debug logging
  useEffect(() => {
    console.log('[ChatButton] Connection status update:', {
      isConnected,
      isAuthenticated,
      user: user?.role,
      hasAdminAccess,
      isInitialized
    });
  }, [isConnected, isAuthenticated, user, hasAdminAccess, isInitialized]);

  // Component mount/unmount logging
  useEffect(() => {
    console.log('[ChatButton] 🎯 ChatButton component mounted');
    return () => {
      console.log('[ChatButton] 💥 ChatButton component unmounted');
    };
  }, []);

  useEffect(() => {
    if (hasAdminAccess && !isInitialized) {
      console.log('[ChatButton] 🚀 Initializing chat for admin user:', user.role);
      console.log('[ChatButton] User object:', user);
      
      // Connect to socket
      console.log('[ChatButton] Calling socketService.connect()...');
      socketService.connect();
      
      // Fetch initial unread count
      console.log('[ChatButton] Fetching initial unread count...');
      dispatch(fetchUnreadCount());
      
      setIsInitialized(true);
      console.log('[ChatButton] ✅ Chat initialization complete');
    } else {
      console.log('[ChatButton] ⏸️ Skipping initialization:', {
        hasAdminAccess,
        isInitialized,
        userRole: user?.role
      });
    }
  }, [hasAdminAccess, user, isInitialized, dispatch]);

  // Separate cleanup effect that only runs on unmount
  useEffect(() => {
    return () => {
      if (isInitialized) {
        console.log('[ChatButton] 🧹 Cleaning up socket connection on unmount...');
        socketService.disconnect();
      }
    };
  }, [isInitialized]);

  // Don't render if user doesn't have admin access
  if (!hasAdminAccess) {
    return null;
  }

  const handleToggleChat = () => {
    if (isWidgetOpen) {
      dispatch(setWidgetOpen(false));
      dispatch(closeActiveConversation());
    } else {
      dispatch(setWidgetOpen(true));
    }
  };

  const getConnectionStatus = () => {
    if (!isConnected) return 'Connecting...';
    if (!isAuthenticated) return 'Authenticating...';
    return 'Connected';
  };

  const isConnectedAndReady = isConnected && isAuthenticated;

  return (
    <>
      {/* Chat Button */}
      <div className="relative group">
        <button
          onClick={handleToggleChat}
          className={`h-8 w-8 md:h-9 md:w-9 flex items-center justify-center rounded-sm border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-colors relative ${
            isWidgetOpen ? 'bg-blue-50 border-blue-300 text-blue-600' : ''
          }`}
          aria-label="Client Messages"
          title={`Client Messages ${!isConnectedAndReady ? `(${getConnectionStatus()})` : ''}`}
        >
          <ChatBubbleLeftRightIcon className="h-4 w-4 md:h-5 md:w-5" />
          
          {/* Connection status indicator */}
          <div className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ${
            isConnectedAndReady ? 'bg-green-400' : 'bg-yellow-400'
          }`} />
          
          {/* Unread count badge */}
          {unreadCount > 0 && (
            <div className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs font-semibold rounded-full flex items-center justify-center border border-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}
        </button>
        
        {/* Tooltip */}
        <span className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:flex text-xs bg-black text-white px-2 py-1 whitespace-nowrap rounded">
          Client Messages {!isConnectedAndReady && `(${getConnectionStatus()})`}
        </span>
      </div>

      {/* Chat Widget Modal */}
      {isWidgetOpen && (
        <ChatWidget />
      )}
    </>
  );
};

export default ChatButton;